/*
 * A reusable usage metric select component
 */
import {
  InputSelectControlField,
  useModalState
} from '@sequencehq/core-components'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { CreateUsageMetricDrawer } from './modals/CreateUsageMetricDrawer'

interface Props
  extends Omit<
    React.ComponentProps<typeof InputSelectControlField>,
    | 'options'
    | 'onChange'
    | 'placeholder'
    | 'initialValue'
    | 'createLabel'
    | 'onCreate'
  > {
  onChange: (customerId: string) => void
  value?: string
  allowCreate?: boolean
  'data-testid'?: string
  isDisabled?: boolean
}

export const UsageMetricSelect = ({
  onChange,
  value,
  allowCreate = true,
  'data-testid': dataTestId,
  label = 'Usage metric',
  ...selectControlProps
}: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()
  const result = useLoadAllItemsQuery(dashboard20240730Client.getUsageMetrics)

  const allUsageMetrics = result.data?.pages?.flatMap(page => page.items) ?? []

  return (
    <>
      <InputSelectControlField
        label={label}
        options={allUsageMetrics.map(usageMetric => ({
          label: `${usageMetric.name} (${usageMetric.aggregationType})`,
          value: usageMetric.id
        }))}
        onChange={onChange}
        placeholder="Find or add a usage metric"
        initialValue={value}
        createLabel={allowCreate ? 'Create new usage metric' : undefined}
        onCreate={() => {
          if (allowCreate) {
            openModal()
          }
        }}
        data-testid={dataTestId ?? 'usageMetricSelect'}
        disabled={selectControlProps.isDisabled}
        {...selectControlProps}
      />

      <CreateUsageMetricDrawer
        isOpen={isModalOpen}
        onClose={closeModal}
        onCreate={usageMetric => {
          onChange(usageMetric.id)
        }}
      />
    </>
  )
}
